import React, { useState } from 'react';
import classnames from 'classnames';
import CTSection from './CTSection';
import { loadTestData } from '../state';
import {
  NeuroMessage,
  Question,
  useNeuroMessages,
  useQuestionContext,
} from '../../components';
import useNeuroState, {useDispatch} from '../../state';
import {
  buildPageUrl,
  lowercaseFirstChar,
  ConversationTestModulePropTypes,
} from '../../utils';
import { Search } from '../../../neuro-ui';

const CTQuestionFlow = ({
  enableAppkeys,
  enablePageInfo,
  isFullHeight,
  width,
}) => {
  const dispatch = useDispatch();
  const {
    testPageData = {},
    user: { uuid }
  } = useNeuroState();
  const questionComponents = useQuestionContext();
  const messageContext = useNeuroMessages();
  const [questionFilter, setQuestionFilter] = useState('');

  const onFilterChange = (v) => {
    setQuestionFilter(v);
  };

  const filterQuestions = (question) => {
    const filter = questionFilter.toLowerCase();
    const message = messageContext.formatIntlMessage({ id: question.appkey }).toString().toLowerCase();
    const appkey = question.appkey.toLowerCase();
    return filter.length >= 3
      ? message.includes(filter) || appkey.includes(filter)
      : true;
  };

  const renderQuestionGroup = ({ questionGroup, page, path }) => {
    return (questionGroup.questions || [])
      .filter(filterQuestions)
      .map((question) => {
        const questionProps = {
          appkey: question.appkey,
          page,
          questionGroup,
          question: {
            ...question,
            attributes: {
              ...question.attributes,
              valueChanged: ({ value, saveChangedValue }) => {
                saveChangedValue(value);
                dispatch(loadTestData());
              },
            },
          },
        };

        const isCalc = question.type === 'calculated';
        const QuestionComponent = questionComponents.getQuestionComponent(
          question.type
        );
        const questionClasses = classnames(
          'question',
          `${lowercaseFirstChar(question.type)}Question`,
          {
            'question--disabled': !question.satisfied,
          }
        );

        const renderQuestionInformation = () => {
          return (
            <div className="question-container__appkeyInfo">
              {enableAppkeys && (
                <div>
                  <NeuroMessage id="convTest.appkeys.question" tagName="label"/>
                  <span>{question.appkey}</span>
                </div>
              )}
              {enableAppkeys && (
                <div>
                  <NeuroMessage id="convTest.appkeys.answers" tagName="label" />
                  {(questionProps?.question?.answers || []).map((answer) => {
                    const messageKey = `${questionProps.appkey}.${answer.key}`;
                    return (
                      <span key={answer.key}>
                        <u>{answer.key}</u> -{' '}
                        <i>
                          <NeuroMessage id={messageKey} />
                        </i>
                      </span>
                    );
                  })}
                </div>
              )}
              {enablePageInfo && (
                <div>
                  <NeuroMessage id="convTest.appkeys.pathKey" tagName="label" />
                  <span>{path.key}</span>
                </div>
              )}
              {enablePageInfo && (
                <div>
                  <NeuroMessage id="convTest.appkeys.pageKey" tagName="label" />
                  <a
                    href={buildPageUrl({ type: 'survey', uuid, page, path })}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {page.key} <i className="fa fa-external-link" />
                  </a>
                </div>
              )}
            </div>
          );
        };

        return (
          <div className="question-container" key={question.appkey}>
            <div className="question-container__labels">
              {question.satisfied ? null : (
                <div className="question-container__label badge badge-dark">
                  Unsatisfied
                </div>
              )}
            </div>
            <Question
              component={QuestionComponent}
              className={questionClasses}
              {...(isCalc && { isQA: true })}
              {...questionProps}
            />
            {(enableAppkeys || enablePageInfo) && renderQuestionInformation()}
          </div>
        );
      });
  };

  const questionFlow = (testPageData.questionPaths || []).map((path) => {
    return (
      <section className="questionPath" key={path.key} id={`${path.key}-path`}>
        {(path.pages || []).map((page) => {
          return (page.questionGroups || []).map((group) =>
            renderQuestionGroup({
              questionGroup: group,
              page,
              path,
            })
          );
        })}
      </section>
    );
  });

  const questionSearchBar = (
    <Search onSearchChange={onFilterChange} placeholder="Filter Questions" />
  );

  return (
    <CTSection
      isFullHeight={isFullHeight}
      width={width}
      title="Question Flow"
      titleContent={questionSearchBar}
    >
      {questionFlow}
    </CTSection>
  );
};

CTQuestionFlow.propTypes = {
  ...ConversationTestModulePropTypes,
};
export default CTQuestionFlow;
